import { useCallback } from 'react';
import { useTelemetryContext } from '../contexts/TelemetryContext';
import logger from '../utils/logger';
import { API_BASE_URL } from '../config/api';

// Helper function to get auth token from localStorage
const getAuthToken = (): string | null => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('tb_token');
  }
  return null;
};

export interface FlowThreshold {
  id: number;
  thresholdName: string;
  warningThreshold: number;
  criticalThreshold: number;
  measurementUnit: string;
  description: string;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateFlowThresholdRequest {
  warningThreshold: number;
  criticalThreshold: number;
  description?: string;
}

interface UpdateThresholdResponse {
  success: boolean;
  data: FlowThreshold;
  message: string;
  error?: string;
}

interface FlowThresholdsResponse {
  success: boolean;
  data: FlowThreshold[];
  error?: string;
}

/**
 * Hook to access flow thresholds from DataManager (centralized data fetching)
 *
 * IMPORTANT: Uses DataManager to prevent excessive API calls.
 * Previously, 23 components were each making their own /api/flow-thresholds request
 * on mount, causing ThingsBoard rate limiting (429 errors).
 *
 * Now: Single fetch via DataManager, shared across all components.
 */
export function useFlowThresholds() {
  // Get data from centralized TelemetryContext (single fetch, shared state)
  const { flowThresholds, loading, error, refetch: fetchThresholds } = useTelemetryContext();
  const thresholds = flowThresholds;

  const updateThreshold = useCallback(async (
    thresholdName: string,
    updates: UpdateFlowThresholdRequest
  ): Promise<boolean> => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/flow-thresholds/${thresholdName}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify(updates),
      });

      const data: UpdateThresholdResponse = await response.json();

      if (data.success) {
        logger.info('Flow threshold updated successfully', 'useFlowThresholds', {
          thresholdName,
          updates
        });
        // Refresh data from server to ensure consistency
        await fetchThresholds();
        return true;
      } else {
        logger.error('Failed to update flow threshold', 'useFlowThresholds', {
          thresholdName,
          error: data.error
        });
        return false;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      logger.error('Error updating flow threshold', 'useFlowThresholds', {
        thresholdName,
        error: errorMessage
      });
      return false;
    }
  }, [fetchThresholds]);

  const resetThresholds = useCallback(async (): Promise<boolean> => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE_URL}/api/flow-thresholds/reset`, {
        method: 'POST',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });

      const data: FlowThresholdsResponse & { message?: string } = await response.json();

      if (data.success) {
        logger.info('Flow thresholds reset to defaults successfully', 'useFlowThresholds');
        // Refresh data from server to ensure consistency
        await fetchThresholds();
        return true;
      } else {
        logger.error('Failed to reset flow thresholds', 'useFlowThresholds', { error: data.error });
        return false;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      logger.error('Error resetting flow thresholds', 'useFlowThresholds', { error: errorMessage });
      return false;
    }
  }, [fetchThresholds]);

  return {
    thresholds: thresholds || [],
    loading,
    error,
    fetchThresholds,
    updateThreshold,
    resetThresholds,
  };
}
